import { useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptionDetails } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { getToken } from '../util/tokenCookies';

export const useSubscriptionEndDate = (enabled = true) => {
    const authContext = useContext(AuthContext);
    const token = getToken();

    // Fallback to token check if context isn't available yet
    const isAuthenticated = authContext?.isAuthenticated ?? Boolean(token);

    const { data: subscriptionDetails, isLoading } = useQuery({
        queryKey: ['subscriptionDetails'],
        queryFn: getSubscriptionDetails,
        enabled: isAuthenticated && enabled,
        staleTime: 1000 * 60 * 5,
    });

    // Next payment date (current_period_end, fallback to subscription_end_date)
    const nextPaymentDate = useMemo(() => {
        if (!subscriptionDetails) return null;

        const currentPeriodEnd = subscriptionDetails.current_period_end;
        const subscriptionEndDate = subscriptionDetails.subscription_end_date;

        const rawDate = currentPeriodEnd || subscriptionEndDate;
        if (!rawDate) return null;

        const dateMs = rawDate > 10000000000 ? rawDate : rawDate * 1000;

        return new Date(dateMs);
    }, [subscriptionDetails]);

    // Access end date (commitment_end_date or current_period_end/subscription_end_date if past commitment)
    const accessEndDate = useMemo(() => {
        if (!subscriptionDetails) return null;

        const now = Date.now();
        const commitmentEnd = subscriptionDetails.commitment_end_date;
        const currentPeriodEnd = subscriptionDetails.current_period_end;
        const subscriptionEndDate = subscriptionDetails.subscription_end_date;

        const rawDate = currentPeriodEnd || subscriptionEndDate;
        if (!rawDate) return null;

        const dateMs = rawDate > 10000000000 ? rawDate : rawDate * 1000;

        if (commitmentEnd) {
            const commitmentEndMs = commitmentEnd > 10000000000 ? commitmentEnd : commitmentEnd * 1000;
            if (now < commitmentEndMs) {
                return new Date(commitmentEndMs);
            }
        }

        return new Date(dateMs);
    }, [subscriptionDetails]);

    const formatDate = (date: Date | null) => {
        if (!date) return null;
        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formattedNextPaymentDate = useMemo(() => formatDate(nextPaymentDate), [nextPaymentDate]);
    const formattedAccessEndDate = useMemo(() => formatDate(accessEndDate), [accessEndDate]);

    // Keep backwards compatibility
    const endDate = nextPaymentDate;
    const formattedDate = formattedNextPaymentDate;

    return {
        endDate,
        formattedDate,
        nextPaymentDate,
        formattedNextPaymentDate,
        accessEndDate,
        formattedAccessEndDate,
        isLoading: isLoading || !subscriptionDetails,
        subscriptionDetails
    };
};
